import {
  ACTIVITIES,
  ACTIVITY_GROUPS,
  BRAND,
  BUYERS,
  countryOptions,
  DEFAULT_COUNTRY,
  isCountry,
  type Account,
  TRIAL_DAYS,
  t,
} from '@procumi/shared';
import { useMemo, useState } from 'react';
import { ErrorBox, Loading } from '../components/StateBox';
import { useAsync } from '../hooks/useAsync';
import { api, errMsg } from '../lib/api';
import { lang } from '../lib/i18n';
import { rich } from '../lib/rich';

/**
 * Țara propusă: după fusul orar (un român cu browserul în engleză e tot în
 * România), apoi după limba browserului. Omul o poate schimba oricum.
 */
function guessCountry(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz === 'Europe/Bucharest') return 'RO';
    if (tz === 'Europe/Chisinau') return 'MD';
    const region = navigator.language.split('-')[1]?.toUpperCase();
    if (region && isCountry(region)) return region;
  } catch {
    /* fără Intl: România */
  }
  return DEFAULT_COUNTRY;
}

/** Termenii și politica de pe site, în limba ecranului. */
function legalUrl(doc: 'terms' | 'privacy'): string {
  const en = lang() === 'en';
  if (doc === 'terms') return en ? 'https://procumi.com/en/terms.html' : 'https://procumi.com/termeni.html';
  return en ? 'https://procumi.com/en/privacy.html' : 'https://procumi.com/confidentialitate.html';
}

/**
 * Înscrierea unei firme noi: de pe pagina deschisă sau din linkul primit de la
 * Procumi. Doi pași: formularul, apoi codul de pe mail. Contul se face abia
 * după cod, ca emailul să fie sigur al celui care se înscrie.
 */
export function SignupScreen({
  token: linkToken,
  onDone,
  onGiveUp,
}: {
  /** linkul de la Procumi; lipsește la înscrierea deschisă */
  token: string | null;
  onDone: (account: Account) => void;
  /** Link expirat sau „Am deja cont": ecranul obișnuit de intrare. */
  onGiveUp: () => void;
}) {
  const { error, loading } = useAsync(
    () => (linkToken ? api.signupInfo(linkToken) : Promise.resolve({ ok: true as const })),
    [linkToken],
  );
  /** Linkul pe care merge codul: al tău, sau cel făcut de server la primul cod. */
  const [token, setToken] = useState<string | null>(linkToken);
  const countries = useMemo(countryOptions, []);
  const [country, setCountry] = useState(guessCountry);
  const [activity, setActivity] = useState('');
  const [buyers, setBuyers] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [terms, setTerms] = useState(false);
  /** Emailul pe care a plecat codul; până atunci, suntem la formular. */
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [problema, setProblema] = useState<string | null>(null);
  const [aviz, setAviz] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const trimiteCod = async () => {
    setBusy(true);
    setProblema(null);
    setAviz(null);
    try {
      const r = await api.signupStart({
        ...(token ? { token } : {}),
        companyName,
        name,
        email,
        password,
        country,
        activity,
        buyers,
        acceptTerms: true,
      });
      if (sentTo) setAviz(t('Am trimis alt cod. Folosește-l pe cel din ultimul mail.'));
      setToken(r.token);
      setSentTo(r.email);
      setCode('');
    } catch (err) {
      setProblema(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const confirma = async () => {
    setBusy(true);
    setProblema(null);
    setAviz(null);
    try {
      onDone(await api.signupVerify({ token: token ?? '', code }));
    } catch (err) {
      setProblema(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!sentTo && (!activity || !buyers)) {
      setProblema(t('Spune-ne ce face firma și câți oameni fac achizițiile.'));
      return;
    }
    if (!sentTo && !terms) {
      setProblema(t('Bifează că ești de acord cu termenii și politica de confidențialitate.'));
      return;
    }
    void (sentTo ? confirma() : trimiteCod());
  };

  const inapoiLaFormular = () => {
    // Emailul se poate schimba: la înscrierea deschisă, un formular nou face alt link.
    if (!linkToken) setToken(null);
    setSentTo(null);
    setCode('');
    setProblema(null);
    setAviz(null);
  };

  return (
    <div className="auth">
      <form className="auth-card" onSubmit={submit}>
        <div className="auth-brand">
          <img className="logo" src="/brand/procumi-lockup.svg" alt={BRAND.name} />
          <small>{BRAND.tagline}</small>
        </div>

        <div className="sheet pad" style={{ display: 'grid', gap: 14 }}>
          {loading ? <Loading /> : null}

          {!loading && error ? (
            <>
              <ErrorBox error={error} />
              <button className="btn" type="button" onClick={onGiveUp}>
                {t('Mergi la ecranul de intrare')}
              </button>
            </>
          ) : null}

          {!loading && !error && !sentTo ? (
            <>
              <div>
                <h2>{t('Cont nou')}</h2>
                <p className="hint" style={{ marginTop: 6 }}>
                  {t(
                    'Contul face o firmă nouă, cu tine ca primul utilizator. Ai {0} zile gratuite, fără card, cu tot ce are pachetul Echipă. Colegii îi inviți apoi din aplicație.',
                    TRIAL_DAYS,
                  )}
                </p>
              </div>
              <label className="f" htmlFor="signup-company">
                {t('Firma ta')}
                <input
                  id="signup-company"
                  className="inp"
                  value={companyName}
                  placeholder={t('ex. Construct Brașov SRL')}
                  autoComplete="organization"
                  onChange={(e) => setCompanyName(e.target.value)}
                />
              </label>
              <label className="f" htmlFor="signup-country">
                {t('Țara firmei')}
                <select
                  id="signup-country"
                  className="inp"
                  value={country}
                  autoComplete="country"
                  onChange={(e) => setCountry(e.target.value)}
                >
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="f" htmlFor="signup-activity">
                {t('Ce face firma ta?')}
                <select
                  id="signup-activity"
                  className="inp"
                  value={activity}
                  onChange={(e) => setActivity(e.target.value)}
                >
                  <option value="" disabled>
                    {t('Alege…')}
                  </option>
                  {ACTIVITY_GROUPS.map((g) => (
                    <optgroup key={g.id} label={g.label}>
                      {ACTIVITIES.filter((a) => a.group === g.id).map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <span className="hint" style={{ margin: 0 }}>
                  {t('Îți propunem categoriile de furnizori potrivite.')}
                </span>
              </label>
              <label className="f" htmlFor="signup-buyers">
                {t('Câți oameni fac achizițiile în firmă?')}
                <select
                  id="signup-buyers"
                  className="inp"
                  value={buyers}
                  onChange={(e) => setBuyers(e.target.value)}
                >
                  <option value="" disabled>
                    {t('Alege…')}
                  </option>
                  {BUYERS.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="f" htmlFor="signup-name">
                {t('Numele tău')}
                <input
                  id="signup-name"
                  className="inp"
                  value={name}
                  placeholder={t('ex. Alex Ionescu')}
                  autoComplete="name"
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className="f" htmlFor="signup-email">
                {t('Email')}
                <input
                  id="signup-email"
                  className="inp"
                  type="email"
                  value={email}
                  autoComplete="username"
                  onChange={(e) => setEmail(e.target.value)}
                />
                <span className="hint" style={{ margin: 0 }}>
                  {t('Îți trimitem aici un cod, ca să știm că e al tău.')}
                </span>
              </label>
              <label className="f" htmlFor="signup-password">
                {t('Parolă')}
                <span className="pw-wrap">
                  <input
                    id="signup-password"
                    className="inp"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    autoComplete="new-password"
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    className="pw-toggle"
                    type="button"
                    aria-pressed={showPassword}
                    aria-controls="signup-password"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? t('Ascunde') : t('Arată')}
                  </button>
                </span>
                <span className="hint" style={{ margin: 0 }}>
                  {t('Cel puțin 8 caractere.')}
                </span>
              </label>
              <label className="terms-check" htmlFor="signup-terms">
                <input
                  id="signup-terms"
                  type="checkbox"
                  checked={terms}
                  onChange={(e) => setTerms(e.target.checked)}
                />
                <span>
                  {rich(
                    'Sunt de acord cu {0} și cu {1}.',
                    <a href={legalUrl('terms')} target="_blank" rel="noopener noreferrer">
                      {t('Termenii și condițiile')}
                    </a>,
                    <a href={legalUrl('privacy')} target="_blank" rel="noopener noreferrer">
                      {t('Politica de confidențialitate')}
                    </a>,
                  )}
                </span>
              </label>
            </>
          ) : null}

          {!loading && !error && sentTo ? (
            <>
              <div>
                <h2>{t('Scrie codul primit pe mail')}</h2>
                <p className="hint" style={{ marginTop: 6 }}>
                  {rich(
                    'L-am trimis pe <b>{0}</b>. Merge 15 minute. Dacă nu-l vezi în câteva minute, uită-te și în Spam.',
                    sentTo,
                  )}
                </p>
              </div>
              <label className="f" htmlFor="signup-code">
                {t('Codul de 6 cifre')}
                <input
                  id="signup-code"
                  className="inp"
                  value={code}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={12}
                  onChange={(e) => setCode(e.target.value)}
                />
              </label>
            </>
          ) : null}

          {aviz ? (
            <div className="auth-ok" role="status">
              {aviz}
            </div>
          ) : null}
          {problema ? (
            <div className="auth-err" role="alert">
              {problema}
            </div>
          ) : null}

          {!loading && !error ? (
            <>
              <button className="btn primary" type="submit" disabled={busy}>
                {busy
                  ? sentTo
                    ? t('Se verifică…')
                    : t('Se trimite…')
                  : sentTo
                    ? t('Confirmă și intră')
                    : t('Trimite-mi codul')}
              </button>
              {sentTo ? (
                <>
                  <button
                    className="auth-link"
                    type="button"
                    disabled={busy}
                    onClick={() => void trimiteCod()}
                  >
                    {t('Trimite alt cod')}
                  </button>
                  <button className="auth-link" type="button" onClick={inapoiLaFormular}>
                    {t('Am greșit emailul')}
                  </button>
                </>
              ) : (
                <button className="btn" type="button" onClick={onGiveUp}>
                  {t('Am deja cont')}
                </button>
              )}
            </>
          ) : null}
        </div>
      </form>
    </div>
  );
}
